import { Stack } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getTravelDocumentKindsOptions,
  useTravelRequestCreate,
} from '@/module/Travel/Request/model/TravelRequestQuery';
import {
  TravelRequestSchema,
  defaultTravelRequestValues,
  toTravelRequestRequest,
  travelRequestSchema,
} from '@/module/Travel/Request/model/TravelRequestSchema';
import { hasTravelMobilitySupport } from '@/module/Travel/Request/model/TravelRequestTypes';

import { useAppNotification } from '@feature/utils';

import { TravelRequestForm } from './TravelRequestForm';
import { travelCreatedLandingStepId } from './components/TravelProcessTimeline';

export function TravelRequestCreate() {
  const { t } = useTranslation();
  const { mutate, isPending, isError, isSuccess } = useTravelRequestCreate();
  // The mutation settles before the landing step is known, so the form keeps submitting until it navigates.
  const [landing, setLanding] = useState(false);

  const queryClient = useQueryClient();
  const notification = useAppNotification();
  const navigate = useNavigate();

  const onSubmit = (values: TravelRequestSchema) => {
    mutate(toTravelRequestRequest(values), {
      onError: notification.error,
      onSuccess: async created => {
        setLanding(true);
        notification.success();
        const documentKinds = await queryClient
          .fetchQuery({
            ...getTravelDocumentKindsOptions(created.activityType, hasTravelMobilitySupport(created)),
            staleTime: 5000,
          })
          .catch(() => undefined);
        // Back from the saved draft would reopen an empty create form.
        navigate({
          to: '/travel/requests/$requestId',
          params: { requestId: String(created.id) },
          hash: travelCreatedLandingStepId(created, documentKinds),
          // The view scrolls once its queries settle; the router would scroll earlier, before the layout stops moving.
          hashScrollIntoView: false,
          replace: true,
        });
      },
    });
  };

  return (
    <Stack>
      <TravelRequestForm
        title={t('travel.requests.form.title.create')}
        schema={travelRequestSchema}
        defaultValues={defaultTravelRequestValues}
        values={undefined}
        withRequiredNote
        isSubmitting={isPending || landing}
        isError={isError}
        isSuccess={isSuccess}
        onSubmit={onSubmit}
        onCancel={() => navigate({ to: '..' })}
      />
    </Stack>
  );
}
